import { SoulEntry, EntryTake } from '../../types';

export interface SoulClipItem {
  id: string;
  title: string;
  duration: number;
  trimStart: number;
  trimEnd: number;
  posterUrl: string;
  videoUrl: string;
  sourceUrl?: string;
  audioUrl?: string;   // original mixed audio / legacy BGM fallback
  bgmUrl?: string;     // explicit BGM stem (Demucs bgm.mp3)
  vocalsUrl?: string;  // explicit vocals stem (Demucs vocals.mp3)
  r2Key?: string;
  createdAt?: number;
  parentClipId?: string;
  isPreset?: boolean;
  takeCount?: number;
  dialogueCount?: number;
  hasStems?: boolean;
  latestPerformer?: string;
  userName?: string;
  creatorName?: string;
  creatorAvatar?: string;
  isShared?: boolean;
  shareVisibility?: 'public' | 'direct' | 'external' | 'private';
  sharedToUsers?: string[];
  sharedAt?: number;
  shareMessage?: string;
  viewsCount?: number;
  likesCount?: number;
  dubbedLinesCount?: number;
  totalLinesCount?: number;
  isEditable?: boolean;
  exportedVideoUrl?: string;
  entry?: SoulEntry | null;
  // Playback data carried from shared_clips / publish_snapshot
  takes?: EntryTake[];
  activeTakes?: EntryTake[];
  dialogueLines?: import('../../types').DialogueLine[];
}

export function formatTime(secs: number): string {
  if (!Number.isFinite(secs) || secs < 0) return '0:00.0';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  const ms = Math.floor((secs % 1) * 10);
  return `${m}:${s < 10 ? '0' : ''}${s}.${ms}`;
}

export function formatTimeSimple(secs: number): string {
  const m = Math.floor(secs / 60);
  const sec = Math.floor(secs % 60);
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
}

// Strictly enforces that AT MOST ONE take per dialogue line has isActive: true
// Allows lines to have 0 active takes if the user deactivated all takes for that line
export function sanitizeTakes(rawTakes: EntryTake[]): EntryTake[] {
  if (!rawTakes || rawTakes.length === 0) return [];
  const activeTakePerLine = new Map<string, EntryTake>();

  // Pass 1: Find explicitly active takes per line (prefer newest/highest takeNumber)
  for (const take of rawTakes) {
    const lineKey = take.lineIds?.[0] || 'default';
    if (take.isActive) {
      const existing = activeTakePerLine.get(lineKey);
      if (!existing || (take.takeNumber || 0) >= (existing.takeNumber || 0)) {
        activeTakePerLine.set(lineKey, take);
      }
    }
  }

  // Enforce single-active rule: only the chosen active take stays active, others are false
  return rawTakes.map((take) => {
    const lineKey = take.lineIds?.[0] || 'default';
    const shouldBeActive = activeTakePerLine.get(lineKey)?.id === take.id;
    return take.isActive === shouldBeActive ? take : { ...take, isActive: shouldBeActive };
  });
}
